import { useCallback, useState } from "react";
import { PanelResizeHandle } from "react-resizable-panels";
import styles from "./CustomResizeHandle.module.css";
import { LucideIcon } from "../../icon/LucideIcon";
import React from "react";

interface CustomResizeHandleProps {
  className?: string;
  id?: string;
  orientation?: "horizontal" | "vertical";
  style?: React.CSSProperties;
}

export const CustomResizeHandle = ({ id, className = "", orientation, style }: CustomResizeHandleProps) => {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <PanelResizeHandle
      className={[styles.ResizeHandleOuter, className].join(" ")}
      id={id}
      onDragging={setIsDragging}
      style={style}
    >
      <CustomResizeHandleCore isDragging={isDragging} orientation={orientation} />
    </PanelResizeHandle>
  );
};

interface CustomResizeHandleCoreProps {
  isDragging: boolean;
  orientation?: "horizontal" | "vertical";
  style?: React.CSSProperties;
}

const CustomResizeHandleCore = ({ isDragging, orientation = "horizontal", style }: CustomResizeHandleCoreProps) => {
  return (
    <div
      className={styles.ResizeHandleInner}
      data-is-dragging={isDragging}
      style={{
        ...(orientation === "horizontal" && { width: 5, top: 0, left: "-2.5px" }),
        ...(orientation === "vertical" && { height: 5, top: "-2.5px", left: 0 }),
        ...style,
      }}
    >
      <div
        className={styles.IconWrapper}
        style={{
          ...(orientation === "horizontal" && { left: "calc(50% - 10px)", top: "calc(50% - 10px)" }),
          ...(orientation === "vertical" && { left: "calc(50% - 10px)", top: "calc(50% - 10px)" }),
        }}
      >
        <LucideIcon
          className={styles.Icon}
          name={orientation === "horizontal" ? "unfold-horizontal" : "unfold-vertical"}
          size={24}
        />
      </div>
    </div>
  );
};

interface FullyCustomResizeHandleProps {
  targetRef: React.RefObject<HTMLDivElement>;
  onChange?: (dw: number, dh: number, w: number, h: number) => void;
  orientation?: "horizontal" | "vertical";
  style?: React.CSSProperties;
}
/**
 * A fully customizable resize handle component that allows resizing of a target element
 * by dragging the handle. It supports both horizontal and vertical orientations.
 * @author @CorradoSurmanowicz
 * @param {FullyCustomResizeHandleProps} props - The properties for the resize handle.
 * @param {React.RefObject<HTMLElement>} props.targetRef - A reference to the target element
 * that will be resized.
 * @param {(deltaW: number, deltaH: number, newWidth: number, newHeight: number) => void} [props.onChange] -
 * A callback function triggered during resizing. It provides the width and height deltas
 * (`deltaW`, `deltaH`) and the new dimensions (`newWidth`, `newHeight`) of the target element.
 * @param {"horizontal" | "vertical"} props.orientation - The orientation of the resize handle.
 * Determines the cursor style and resizing behavior. Use `"horizontal"` for left-right resizing
 * and `"vertical"` for top-bottom resizing.
 * @param {React.CSSProperties} [props.style] - Additional styles to apply to the resize handle.
 *
 * @returns {JSX.Element} A resize handle component that can be dragged to resize the target element.
 */
export const FullyCustomResizeHandle = ({ targetRef, onChange, orientation, style }: FullyCustomResizeHandleProps) => {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!targetRef.current) return;

        const deltaW =
          ((e.clientX - targetRef.current.getBoundingClientRect().left) /
            targetRef.current.getBoundingClientRect().width) *
          100;

        const deltaH =
          ((e.clientY - targetRef.current.getBoundingClientRect().bottom) /
            targetRef.current.getBoundingClientRect().height) *
          100;

        const h =
          targetRef.current.getBoundingClientRect().height +
          (deltaH / 100) * targetRef.current.getBoundingClientRect().height;

        const w =
          targetRef.current.getBoundingClientRect().width +
          (deltaW / 100) * targetRef.current.getBoundingClientRect().width;

        // console.log(deltaH, deltaW, h, w);
        onChange?.(deltaW, deltaH, w <= 0 ? 0 : w, h <= 0 ? 0 : h);
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [onChange, targetRef]
  );

  return (
    <div
      style={{
        flex: "0 0",
        position: "relative",
        backgroundColor: "transparent",
        outline: "0.5px solid var(--gray-200)",
        cursor: orientation === "horizontal" ? "ow-resize" : "ns-resize",
        userSelect: "none",
        ...style,
      }}
      onMouseDown={(e) => handleMouseDown(e)}
    >
      <CustomResizeHandleCore isDragging={false} orientation={orientation} />
    </div>
  );
};
